import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { message } from "antd";

import { Button, Input, Pagination, Select } from "../../../shared";
import DataTableUntyped from "../../../shared/dashboards/DataTable/DataTable";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { useDebouncedValue } from "../../../../hooks/useDebouncedSearchQuery";
import {
  mapCodeMetricsToRow,
  type AffiliateRow,
} from "../../affiliatesData";
import {
  LinkStatusBadge,
  buildPromoCodeColumns,
  getVisiblePromoCodeColumns,
} from "../../sharedPromoCodeColumns";
import type { ShareableColumnId } from "../../NewAffiliate/AffiliatePamAccount/affiliatePamAccountData";
import useCurrencySymbol from "../../../../hooks/useCurrencySymbol";
import { useAffiliate } from "../../../../queries/affiliates/useAffiliate";
import CreatePromoCodeModal from "../../NewAffiliate/PromoCodes/CreatePromoCodeModal/CreatePromoCodeModal";
import type { CreatePromoCodeFormValues } from "../../NewAffiliate/PromoCodes/CreatePromoCodeModal/createPromoCodeModalData";
import {
  useAddPromoCodes,
  useAffiliatePromoCodes,
} from "../../../../queries/affiliates/useAffiliatePromoCodes";
import { mapPromoCodesToPayload } from "../../../../queries/affiliates/affiliateMappers";
import type {
  AffiliatePromoCodeDetail,
  PromoCodeLinkStatus,
} from "../../../../types/affiliates";

import styles from "./AffiliatePromoCodes.module.css";
import { getAdminNow } from "../../../../utils/dateService";

type AffiliateCodesTableProps = {
  columns: Array<Record<string, unknown>>;
  dataSource: AffiliateRow[];
  rowKey: string;
  scrollPreset?: string;
  loading?: boolean;
  pagination?: false | Record<string, unknown>;
};
const TypedDataTable =
  DataTableUntyped as unknown as FC<AffiliateCodesTableProps>;

export type AffiliatePromoCodesProps = {
  affiliateId?: string;
  // ISO YYYY-MM-DD range from the AffiliateProfile header date picker.
  // Falls back to month-to-date when not supplied.
  startDate?: string;
  endDate?: string;
  // portal_view_config selection — set by the portal preview so the table
  // shows exactly the columns the affiliate will get (same gate the portal
  // applies). Undefined → full catalogue (operator profile default).
  selectedColumns?: ReadonlyArray<ShareableColumnId>;
  // Preview surfaces mirror the portal, where affiliates can't create
  // codes — hides the New Code button and its modal.
  readOnly?: boolean;
};

type PromoCodeRow = AffiliateRow & {
  linkStatus?: PromoCodeLinkStatus;
  // AppsOnAir URL for the App Link column (`appsonair_link_url` from BE).
  appLink?: string | null;
  reason?: string;
  // BE doesn't surface this yet; the column renders "-" until the
  // promo-code detail payload starts threading the welcome bonus through.
  welcomeBonus?: string | null;
};

const promoCodeToRow = (
  code: AffiliatePromoCodeDetail,
  affiliateId: string,
  // CPA Amt override per affiliate plan: number = configured rate,
  // null = force N/A, undefined = leave the per-code KPI alone.
  cpaOverride: number | null | undefined,
): PromoCodeRow => {
  const metrics = mapCodeMetricsToRow(code.metrics);
  if (cpaOverride !== undefined) metrics.cpaAmount = cpaOverride;
  return {
    key: code.promo_code,
    id: affiliateId,
    referralCode: code.promo_code,
    ...metrics,
    linkStatus: code.link_status ?? undefined,
    appLink: code.appsonair_link_url,
    reason: code.applink_failure_reason ?? undefined,
    status: code.disabled ? "deactivated" : "active",
  };
};

const AffiliatePromoCodes: FC<AffiliatePromoCodesProps> = ({
  affiliateId,
  startDate,
  endDate,
  selectedColumns,
  readOnly = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [linkStatusFilter, setLinkStatusFilter] = useState<
    PromoCodeLinkStatus | undefined
  >(undefined);
  const [isAddOpen, setAddOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [cursorStack, setCursorStack] = useState<(string | null | undefined)[]>(
    [undefined],
  );

  // Debounce the search so typing doesn't fire one request per keystroke
  // — mirrors the affiliates-list pattern in `Affiliates.tsx`.
  const debouncedSearch = useDebouncedValue(searchValue);

  useEffect(() => {
    setCurrentPage(1);
    setCursorStack((prev) =>
      prev.length === 1 && prev[0] === undefined ? prev : [undefined],
    );
  }, [debouncedSearch, linkStatusFilter, pageSize, startDate, endDate]);

  const lastEvaluatedKey =
    currentPage <= 1 ? undefined : (cursorStack[currentPage - 1] ?? undefined);

  // Scope the per-code metrics + KPI cards to the header date picker, falling
  // back to month-to-date when the parent doesn't supply a range.
  const dateRange = useMemo(
    () => ({
      start_date: startDate ?? getAdminNow().startOf("month").format("YYYY-MM-DD"),
      end_date: endDate ?? getAdminNow().format("YYYY-MM-DD"),
    }),
    [startDate, endDate],
  );

  const promoCodesQuery = useAffiliatePromoCodes(affiliateId, {
    search: debouncedSearch || undefined,
    limit: pageSize,
    last_evaluated_key: lastEvaluatedKey ?? undefined,
    ...dateRange,
  });

  const fakeTotal = useMemo(() => {
    const itemsThisPage = promoCodesQuery.data?.items.length ?? 0;
    const hasNext = Boolean(promoCodesQuery.data?.last_evaluated_key);
    return hasNext
      ? currentPage * pageSize + 1
      : (currentPage - 1) * pageSize + itemsThisPage;
  }, [promoCodesQuery.data, currentPage, pageSize]);

  const nextCursorFromResponse = promoCodesQuery.data
    ? (promoCodesQuery.data.last_evaluated_key ?? null)
    : undefined;
  useEffect(() => {
    if (nextCursorFromResponse === undefined) return;
    setCursorStack((prev) => {
      // Treat unset and null the same — Pagination just needs a truthy
      // cursor to enable Next, so skipping the write avoids an extra render.
      const existing = prev[currentPage] ?? null;
      if (existing === nextCursorFromResponse) return prev;
      const next = [...prev];
      while (next.length <= currentPage) next.push(undefined);
      next[currentPage] = nextCursorFromResponse;
      return next;
    });
  }, [currentPage, nextCursorFromResponse]);
  const addPromoCodes = useAddPromoCodes();

  // The detail query feeds the per-code CPA Amt override (plan-type aware) —
  // the table reuses the date-scoped affiliate-detail entry the parent fetches.
  const detailQuery = useAffiliate(affiliateId, dateRange);

  const planType = detailQuery.data?.current_plan?.plan_type;
  // Hybrid carries its CPA component in the same flat `cpa_payment_per_action`
  // field, so treat it like CPA for the per-code CPA Amt override.
  const cpaOverride: number | null | undefined =
    planType === "CPA" || planType === "HYBRID"
      ? (detailQuery.data?.current_plan?.cpa_payment_per_action ?? undefined)
      : planType === "REVENUE_SHARE"
        ? null
        : undefined;

  const allCodes = useMemo<PromoCodeRow[]>(() => {
    const codes = promoCodesQuery.data?.items ?? [];
    return codes.map((code) =>
      promoCodeToRow(code, affiliateId ?? "", cpaOverride),
    );
  }, [promoCodesQuery.data?.items, affiliateId, cpaOverride]);

  const codesData = useMemo<PromoCodeRow[]>(() => {
    const normalizedSearch = debouncedSearch.trim().toLowerCase();
    return allCodes.filter((code) => {
      if (linkStatusFilter && code.linkStatus !== linkStatusFilter)
        return false;
      if (!normalizedSearch) return true;
      return (
        code.referralCode?.toLowerCase().includes(normalizedSearch) ||
        code.id?.toLowerCase().includes(normalizedSearch) ||
        false
      );
    });
  }, [allCodes, debouncedSearch, linkStatusFilter]);

  const handleAddPromoCode = useCallback(
    async (values: CreatePromoCodeFormValues) => {
      if (!affiliateId) return;
      try {
        await addPromoCodes.mutateAsync({
          affiliateId,
          payload: { promo_codes: mapPromoCodesToPayload([values]) },
        });
        message.success(
          AppStrings.PROMO_CODE_QUEUED.replace("{name}", values.name),
        );
        setAddOpen(false);
      } catch (err) {
        const envelope = (err as { response?: { data?: { message?: string } } })
          ?.response?.data;
        message.error(envelope?.message || AppStrings.PROMO_CODE_ADD_FAILED);
      }
    },
    [affiliateId, addPromoCodes],
  );

  const currencySymbol = useCurrencySymbol() ?? "$";
  const codesColumns = useMemo<Array<Record<string, unknown>>>(() => {
    if (selectedColumns) {
      return getVisiblePromoCodeColumns(selectedColumns, currencySymbol);
    }
    return [
      ...buildPromoCodeColumns(currencySymbol),
      // Local-only column — kept off the shared columns so the affiliates
      // list, the portal preview and the portal-view config toggles
      // aren't forced to render it.
      {
        key: "welcomeBonus",
        title: AppStrings.WELCOME_BONUS,
        dataIndex: "welcomeBonus",
        width: 160,
        render: (value: string | null | undefined) =>
          value && value.trim().length > 0 ? value : "-",
      },
    ];
  }, [currencySymbol, selectedColumns]);

  return (
    <section
      className={styles.container}
      data-testid="affiliate-promo-codes"
      data-affiliate-id={affiliateId ?? ""}
    >
      <div className={styles.tableSection}>
        <h2 className={styles.tableTitle}>
          {AppStrings.AFFILIATE_CODES}{" "}
          <span className={styles.tableTitleCount}>({codesData.length})</span>
        </h2>

        <div className={styles.searchSection}>
          <div className={styles.searchInputs}>
            <Input
              className={styles.searchInput}
              placeholder={AppStrings.SEARCH_BY_CODE}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              allowClear
            />
            <Select<PromoCodeLinkStatus>
              className={styles.statusFilter}
              classNames={{ popup: { root: "affiliates-status-dropdown" } }}
              placeholder={AppStrings.STATUS}
              value={linkStatusFilter}
              onChange={(value) => setLinkStatusFilter(value)}
              allowClear
              options={[
                {
                  value: "provisioned",
                  label: <LinkStatusBadge status="provisioned" />,
                },
                {
                  value: "pending",
                  label: <LinkStatusBadge status="pending" />,
                },
                {
                  value: "failed",
                  label: <LinkStatusBadge status="failed" />,
                },
                {
                  value: "deleted",
                  label: <LinkStatusBadge status="deleted" />,
                },
              ]}
            />
          </div>
          {!readOnly && (
            <Button
              intent="primary"
              size="large"
              iconLeft={<PlusOutlined />}
              onClick={() => setAddOpen(true)}
              disabled={!affiliateId}
              data-testid="affiliate-promo-codes-new-code"
            >
              {AppStrings.NEW_CODE}
            </Button>
          )}
        </div>

        <TypedDataTable
          rowKey="key"
          columns={codesColumns}
          dataSource={codesData}
          scrollPreset="ultraWide"
          loading={
            promoCodesQuery.isLoading ||
            promoCodesQuery.isFetching ||
            detailQuery.isLoading
          }
          pagination={false}
        />
        <Pagination
          total={linkStatusFilter ? codesData.length : fakeTotal}
          pageSize={pageSize}
          currentPage={currentPage}
          onPageSizeChange={(value: number) => {
            setCurrentPage(1);
            setPageSize(value);
          }}
          onPageChange={(nextPage: number) => {
            if (nextPage < currentPage) {
              setCurrentPage(nextPage);
              return;
            }
            if (nextPage === currentPage) return;
            if (promoCodesQuery.data?.last_evaluated_key) {
              setCurrentPage(nextPage);
            }
          }}
          itemLabel="codes"
          restrictToAdjacentPages
        />
      </div>

      {!readOnly && (
        <CreatePromoCodeModal
          open={isAddOpen}
          mode="create"
          index={(promoCodesQuery.data?.items?.length ?? 0) + 1}
          onClose={() => setAddOpen(false)}
          onSubmit={handleAddPromoCode}
        />
      )}
    </section>
  );
};

export default AffiliatePromoCodes;
